"use client"

import { useState } from "react"
import {
  Box,
  TextField,
  Grid,
  Paper,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material"
import LoadingButton from "../common/LoadingButton"

const CustomerForm = ({ customer = {}, onSubmit, loading = false, isEdit = false }) => {
  const [formData, setFormData] = useState({
    name: customer.name || "",
    email: customer.email || "",
    externalId: customer.externalId || "",
    status: customer.status || "active",
    contactPerson: {
      name: customer.contactPerson?.name || "",
      email: customer.contactPerson?.email || "",
      phone: customer.contactPerson?.phone || "",
    },
    address: {
      line1: customer.address?.line1 || "",
      line2: customer.address?.line2 || "",
      city: customer.address?.city || "",
      state: customer.address?.state || "",
      postalCode: customer.address?.postalCode || "",
      country: customer.address?.country || "",
    },
  })

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value,
    })
  }

  const handleNestedChange = (group, field, value) => {
    setFormData({
      ...formData,
      [group]: {
        ...formData[group],
        [field]: value,
      },
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    onSubmit(formData)
  }

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Customer Details
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              label="Customer Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="External ID"
              name="externalId"
              value={formData.externalId}
              onChange={handleChange}
              helperText="Reference ID from your system"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="status-label">Status</InputLabel>
              <Select
                labelId="status-label"
                name="status"
                value={formData.status}
                label="Status"
                onChange={handleChange}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="trial">Trial</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Contact Person
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Contact Name"
              value={formData.contactPerson.name}
              onChange={(e) => handleNestedChange("contactPerson", "name", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Contact Email"
              type="email"
              value={formData.contactPerson.email}
              onChange={(e) => handleNestedChange("contactPerson", "email", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Contact Phone"
              value={formData.contactPerson.phone}
              onChange={(e) => handleNestedChange("contactPerson", "phone", e.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Address
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address Line 1"
              value={formData.address.line1}
              onChange={(e) => handleNestedChange("address", "line1", e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address Line 2"
              value={formData.address.line2}
              onChange={(e) => handleNestedChange("address", "line2", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="City"
              value={formData.address.city}
              onChange={(e) => handleNestedChange("address", "city", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="State/Province"
              value={formData.address.state}
              onChange={(e) => handleNestedChange("address", "state", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Postal Code"
              value={formData.address.postalCode}
              onChange={(e) => handleNestedChange("address", "postalCode", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Country"
              value={formData.address.country}
              onChange={(e) => handleNestedChange("address", "country", e.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <LoadingButton type="submit" variant="contained" loading={loading}>
          {isEdit ? "Update Customer" : "Create Customer"}
        </LoadingButton>
      </Box>
    </Box>
  )
}

export default CustomerForm

